import { Link } from "react-router-dom";
import { Instagram, Telegram, WhatsApp } from "./icons";
import BarcraftLogo from "./BarcraftLogo";
import { useLanguage } from "../context/LanguageContext";
import { useRFQ } from "../context/RFQContext";
import {
  CONTACT_BIN,
  CONTACT_EMAIL,
  CONTACT_INSTAGRAM_URL,
  CONTACT_PHONE,
  CONTACT_PHONE_TEL,
  CONTACT_TELEGRAM_URL,
  CONTACT_WHATSAPP_URL,
} from "../data/contact";
import { resetConsent } from "../lib/cookieConsent";

const SOCIAL_LINKS = [
  { label: "Telegram", href: CONTACT_TELEGRAM_URL, icon: Telegram, size: 34 },
  { label: "WhatsApp", href: CONTACT_WHATSAPP_URL, icon: WhatsApp, size: 33 },
  { label: "Instagram", href: CONTACT_INSTAGRAM_URL, icon: Instagram, size: 33 },
];

export default function Footer() {
  const { t } = useLanguage();
  const { items, openDrawer } = useRFQ();

  const LINKS = [
    { label: t("footerDocShipping"), href: "/shipping-and-payment" },
    { label: t("footerLinkRefund"), href: "/refund-policy" },
    { label: t("footerLinkTerms"), href: "/terms" },
    { label: t("footerLinkPrivacy"), href: "/privacy-policy" },
    { label: t("contacts"), href: "/contacts" },
  ];

  return (
    <footer className="relative z-10 bg-ink py-16 text-white">
      <div className="container-industrial">
        {/* grid-cols-12 only applies from lg: up — at 12 real columns, gap-12
            reserves 11 internal 48px gaps (528px) regardless of content,
            which is wider than a phone viewport can ever supply. Below lg,
            a single-column grid has no internal column gaps to blow out. */}
        <div className="grid grid-cols-1 items-start gap-12 border-b border-white/10 pb-12 lg:grid-cols-12">
          {/* Left: brand, legal entity, contact info, social — one flowing stack. */}
          <div className="flex min-w-0 flex-col gap-5 lg:col-span-5">
            <a href="/" className="inline-block">
              <BarcraftLogo className="h-[37px] w-auto text-white" />
            </a>

            <div className="text-sm leading-relaxed text-white/70">
              <p className="font-semibold text-white/90">{t("footerCompany")}</p>
              <p className="mt-1">
                {t("footerBinLabel")} {CONTACT_BIN}
              </p>
              <p className="mt-1">{t("contactDetailAddressValue")}</p>
            </div>

            <div className="flex flex-col gap-1.5 text-sm text-white/70">
              <a
                href={CONTACT_PHONE_TEL}
                className="transition-colors duration-200 ease-engineered hover:text-accent"
              >
                {CONTACT_PHONE}
              </a>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="transition-colors duration-200 ease-engineered hover:text-accent"
              >
                {CONTACT_EMAIL}
              </a>
              <p>{t("contactDetailHoursValue")}</p>
            </div>

            <div className="flex gap-2">
              {SOCIAL_LINKS.map(({ label, href, icon: Icon, size }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="flex h-9 w-9 shrink-0 items-center justify-center border border-white/20 text-white/70 transition-colors duration-150 ease-engineered hover:border-accent hover:bg-white/10 hover:text-accent active:bg-white/10"
                >
                  <Icon size={size} strokeWidth={1} />
                </a>
              ))}
            </div>
          </div>

          {/* Middle: plain link list, no subheader, plus a one-line terms note. A
              spacer matching the logo's own height (desktop only) puts the link
              list's top flush with "ТОО «БАРКРАФТ (BARCRAFT)»" on the left,
              rather than with the logo above it. */}
          <div className="flex min-w-0 flex-col gap-5 lg:col-span-3">
            <div aria-hidden="true" className="hidden h-[37px] lg:block" />
            <ul className="flex flex-col gap-1.5 text-left">
              {LINKS.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-white/70 transition-colors duration-200 ease-engineered hover:text-accent"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: the RFQ call-to-action. Same top-aligned spacer as the
              links column, so its heading starts on the same line as
              "ТОО «БАРКРАФТ (BARCRAFT)»" and the links list, not centered
              in the row's full height. */}
          <div className="flex min-w-0 flex-col gap-4 lg:col-span-4">
            <div aria-hidden="true" className="hidden h-[37px] lg:block" />
            <div>
              <h3 className="font-sans text-xl font-semibold uppercase leading-tight tracking-tight text-white">
                {t("footerQuoteHeading")}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-white/50">{t("footerQuoteDesc")}</p>
              <button
                type="button"
                onClick={openDrawer}
                className="btn-outline mt-5"
              >
                {t("footerQuoteButton")}
                {items.length > 0 && <span className="text-xs font-semibold">{items.length}</span>}
              </button>
            </div>
          </div>
        </div>

        {/* Bottom bar: one line — copyright on the left, cookie settings on the right. */}
        <div className="flex flex-col gap-3 pt-6 text-xs text-white/40 sm:flex-row sm:items-center sm:justify-between">
          <span>
            &copy; {new Date().getFullYear()} BARCRAFT. {t("footerRights")}.
          </span>
          <button
            type="button"
            onClick={resetConsent}
            className="text-left underline-offset-2 transition-colors duration-200 ease-engineered hover:text-accent hover:underline sm:text-right"
          >
            {t("footerCookieSettings")}
          </button>
        </div>
      </div>
    </footer>
  );
}
